import { getStrings } from './strings';
import type { GradeResult, ObjectiveResult, PublicMission } from '../types';

/** Visible objectives that passed, over the total the learner can see. */
export function gradeCounts(mission: PublicMission, grade: GradeResult | null): { passed: number; total: number } {
  const byId = new Map((grade?.objectives ?? []).map((o) => [o.id, o]));
  let passed = 0;
  for (const o of mission.objectives) if (byId.get(o.id)?.passed) passed++;
  return { passed, total: mission.objectives.length };
}

export type ObjectiveState = 'is-pass' | 'is-fail' | 'is-open' | 'is-skip';

export function objectiveState(r: ObjectiveResult | undefined): ObjectiveState {
  if (!r) return 'is-open';
  if (r.passed) return 'is-pass';
  if (r.skipped) return 'is-skip';
  return 'is-fail';
}

/** Ids of visible objectives that pass in `next` but did not in `prev`. */
export function newlyPassed(mission: PublicMission, prev: GradeResult | null, next: GradeResult | null): string[] {
  if (!next) return [];
  const before = new Map((prev?.objectives ?? []).map((o) => [o.id, o.passed]));
  const visible = new Set(mission.objectives.map((o) => o.id));
  return next.objectives.filter((o) => visible.has(o.id) && o.passed && !before.get(o.id)).map((o) => o.id);
}

/** Hidden checks that ran and failed: the only moment a hidden objective is spoken of at all. */
export function hiddenFailures(grade: GradeResult | null): ObjectiveResult[] {
  return (grade?.objectives ?? []).filter((o) => o.hidden && !o.passed && !o.skipped);
}

/**
 * What the rail says once a check has run. Plain and short: a pass says
 * everything holds, a failure counts what does, and neither says how to fix
 * anything, since the objectives above already say what is wrong. Only the
 * objectives the learner can see are counted; a hidden failure gets one
 * sentence pointing back at the list, where it has just been revealed.
 *
 * Every sentence is built by the catalogue rather than here, and the
 * hidden-check note is passed *into* it rather than concatenated onto the
 * end of it. The reason is the count. English pluralises by appending a
 * letter and can therefore afford to assemble a verdict out of fragments;
 * Arabic has a dual and two separate plural forms, its word order is not
 * English's, and `held + hidden` would put the second sentence in a place
 * that is right in exactly one language. So this function counts, and says
 * nothing (strings/progress.ts, `verdict`).
 *
 * `getStrings()`, not `useStrings()`: this is a plain function called from a
 * render, not a hook. Its caller is what re-renders when the language
 * changes (components/play/MissionBrief.tsx reads the catalogue too).
 */
export function verdict(mission: PublicMission, grade: GradeResult): { headline: string; detail: string } {
  if (grade.practice) {
    const s = getStrings().checkpoint;
    return { headline: s.progressChecked, detail: s.progressDetail };
  }
  const s = getStrings().progress.verdict;
  const { passed, total } = gradeCounts(mission, grade);
  if (grade.passed) return { headline: s.passed, detail: s.allHold(total) };
  const hidden = hiddenFailures(grade).length > 0;
  return {
    headline: s.notYet,
    detail: passed === 0 ? s.noneHold(total, hidden) : s.someHold(passed, total, hidden),
  };
}
